import React, { useState, useEffect } from 'react';
import Spline from '@splinetool/react-spline';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import ParticleBackground from '../components/ParticleBackground';
import { 
  Code, 
  Palette, 
  Zap, 
  MessageSquare, 
  ShoppingCart, 
  Cloud,
  Sparkles,
  Trophy,
  Award,
  CheckCircle2,
  ArrowRight,
  Mail,
  Linkedin,
  Twitter,
  Github,
  Rocket,
  Star,
  Target
} from 'lucide-react';
import { toast } from "sonner";
import '../styles/home.css';
import '../styles/premium-enhancements.css';

const Home = () => {
  const [counts, setCounts] = useState({ websites: 0, automations: 0, satisfaction: 0, experience: 0 });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    budget: '',
    message: ''
  });

  useEffect(() => {
    const targets = { websites: 120, automations: 50, satisfaction: 98, experience: 5 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const timer = setInterval(() => {
      setCounts(prev => {
        const newCounts = { ...prev };
        Object.keys(targets).forEach(key => {
          if (newCounts[key] < targets[key]) {
            newCounts[key] = Math.min(newCounts[key] + Math.ceil(targets[key] / steps), targets[key]);
          }
        });
        return newCounts;
      });
    }, interval);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const services = [
    { icon: <Code size={40} />, title: 'AI-Powered Website Development', description: 'Custom websites built with cutting-edge AI technology for maximum performance and conversion.' },
    { icon: <Sparkles size={40} />, title: '3D & Animated Landing Pages', description: 'Stunning 3D visuals and smooth animations that captivate visitors and drive engagement.' },
    { icon: <Palette size={40} />, title: 'Full Branding & Logo Design', description: 'Complete brand identity creation from logos to comprehensive design systems.' },
    { icon: <MessageSquare size={40} />, title: 'Automation Systems', description: 'AI chatbots and automated workflows that streamline your business operations.' },
    { icon: <ShoppingCart size={40} />, title: 'E-commerce Development', description: 'High-converting online stores with seamless payment integration and inventory management.' },
    { icon: <Cloud size={40} />, title: 'Fast Deployment & Hosting', description: 'Lightning-fast deployment with reliable hosting infrastructure for 99.9% uptime.' }
  ];

 const portfolio = [
  { 
    name: 'searchaa.io', 
    description: 'AI-powered smart search engine with deep ranking algorithms', 
    url: 'https://searchaa.io',
    image: '/portfolio/searchaa.png'
  },
  { 
    name: 'grammaa.com', 
    description: 'An AI-powered grammer checker that helps people improve their grammar and write clear, correct English.', 
    url: 'https://grammaa.com/',
    image: '/portfolio/grammaa.png'
  },
  { 
    name: 'physicsaa.com', 
    description: 'An AI-powered tool that helps students understand and solve physics problems step by step.', 
    url: 'https://physicsaa.com',
    image: '/portfolio/physicsaa.png'
  },
  { 
    name: 'Coming soon', 
    description: 'Coming soon', 
    url: 'https://your-indonesia-orders-tool.com',
    image: '/portfolio/comingsoon3.png'
  }
];

  const whyChooseUs = [
    { title: 'AI-Driven Development', description: 'Leverage cutting-edge AI for smarter, faster solutions' },
    { title: 'Blazing-Fast Delivery', description: 'Get your project live in record time without compromising quality' },
    { title: '3D & Animated UI/UX', description: 'Stunning visuals that create memorable user experiences' },
    { title: 'Fully Custom Systems', description: 'Tailored solutions designed specifically for your needs' },
    { title: 'Conversion-Focused Design', description: 'Every element optimized to drive results and ROI' },
    { title: 'Long-term Support', description: 'Ongoing maintenance and updates to keep you ahead' }
  ];

  const testimonials = [
  {
    name: "Sarah Johnson",
    role: "CEO, TechStart Inc",
    text: "aiwebAA transformed our vision into reality. The AI-powered features exceeded our expectations, and our conversion rate increased by 240%."
  },
  {
    name: "Michael Chen",
    role: "Founder, EcomGrowth",
    text: "The 3D animations and glassmorphism design made our brand stand out. Professional, fast, and absolutely stunning work."
  },
  {
    name: "Alfiyyah Bunga Kamilia",
    role: "Marketing Director, aiwebAA",
    text: "This aiwebAA really makes stunning websites. It helps us a lot. All the features look very clear and do not confuse us. The design of aiwebAA is really amazing. Many other websites will be coming out soon. Stay tuned! Do not forget to check other websites such as grammAA."
  }
];


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await fetch("https://hook.us2.make.com/ipvzqbiyncsewgdqo5orak4l868lny09", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      toast.success("Project Submitted Successfully , Our team will contact you shortly.");

      setFormData({
        name: "",
        email: "",
        projectType: "",
        budget: "",
        message: ""
      });

    } catch (error) {
      toast.error("Error: Something went wrong!");
      console.error(error);
    }
  };

  return (
    <div className="home-container">

      {/* Header */}
      <header className="dark-header">
        <div className="header-content">

          {/* LOGO UPDATED */}
          <div className="logo flex items-center gap-2 cursor-pointer">
            <img 
              src="/logo.png" 
              alt="AIWEBAA Logo"
              className="w-8 h-8 object-cover rounded-full drop-shadow-[0_0_8px_#00ffc8]"
            />
            <span className="text-2xl font-extrabold gradient-text">aiwebAA</span>
          </div>

          <nav className="dark-nav">
            <a href="#services" className="dark-nav-link">Services</a>
            <a href="#portfolio" className="dark-nav-link">Portfolio</a>
            <a href="#contact" className="dark-nav-link">Contact</a>
          </nav>

        </div>
      </header>
      {/* Hero Section */}
      <section className="hero-section">
        <ParticleBackground />
        <div className="aurora-bg"></div>
        <div className="hero-content">
          <div className="hero-text fade-in-up stagger-1">
            <div className="hero-badge pulse-glow">
              <Sparkles size={16} style={{ marginRight: '8px' }} />
              A next-generation agency that blends AI + Creativity + Engineering
            </div>

            <h1 className="hero-title text-reveal">
              <span className="gradient-text">aiwebAA</span> AI-Powered Website Development & Automation Agency
            </h1>

            <p className="hero-subtitle fade-in-up stagger-2">
              We build ultra-modern, automated, high-performance websites that convert.
            </p>

            <div className="hero-buttons fade-in-up stagger-3">
              <Button className="btn-primary magnetic-button" 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                <Rocket size={20} />
                Get Started
                <ArrowRight size={20} />
              </Button>

              <Button className="btn-secondary spotlight" 
                onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
              >
                View Our Work
                <Star size={20} />
              </Button>
            </div>
          </div>

          <div className="hero-spline float-animation fade-in-up stagger-4">
            <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
          </div>
        </div>

        <div className="hero-gradient"></div>
      </section>

      {/* Services */}
      <section id="services" className="services-section">
        <div className="section-header fade-in-up">
          <h2 className="section-title">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="section-subtitle">Comprehensive solutions for modern digital experiences</p>
        </div>

        <div className="services-grid">
          {services.map((service, index) => (
            <Card key={index} className="service-card glass-card-premium neon-border perspective-card">
              <div className="perspective-card-inner">
                <div className="service-icon holographic">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Achievements */}
      <section className="achievements-section">
        <div className="section-header fade-in-up">
          <h2 className="section-title">
            Our <span className="gradient-text">Achievements</span>
          </h2>
        </div>

        <div className="achievements-grid">
          <div className="achievement-card glass-card-premium spotlight tilt-3d">
            <Trophy className="achievement-icon" size={40} />
            <div className="achievement-number gradient-text">{counts.websites}+</div>
            <div className="achievement-label">Websites Delivered</div>
          </div>

          <div className="achievement-card glass-card-premium spotlight tilt-3d">
            <Zap className="achievement-icon" size={40} />
            <div className="achievement-number gradient-text">{counts.automations}+</div>
            <div className="achievement-label">AI Automation Systems Built</div>
          </div>

          <div className="achievement-card glass-card-premium spotlight tilt-3d">
            <Target className="achievement-icon" size={40} />
            <div className="achievement-number gradient-text">{counts.satisfaction}%</div>
            <div className="achievement-label">Client Satisfaction</div>
          </div>

          <div className="achievement-card glass-card-premium spotlight tilt-3d">
            <Award className="achievement-icon" size={40} />
            <div className="achievement-number gradient-text">{counts.experience}</div>
            <div className="achievement-label">Years Experience</div>
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section id="portfolio" className="portfolio-section">
        <div className="section-header fade-in-up">
          <h2 className="section-title">
            Our <span className="gradient-text">Portfolio</span> Highlight
          </h2>
          <p className="section-subtitle">Some of our recent work</p>
        </div>

        <div className="portfolio-grid">
          {portfolio.map((project, index) => (
            <a key={index} 
               href={project.url} 
               target="_blank" 
               rel="noopener noreferrer" 
               style={{ textDecoration: 'none' }}
            >
              <Card className="portfolio-card glass-card-premium neon-border perspective-card">
                <div className="perspective-card-inner">
                  <div className="portfolio-image holographic">
                    <img 
  src={project.image} 
  alt={project.name}
  className="portfolio-image-thumb"
  />
                    <div className="scanline"></div>
                  </div>

                  <h3 className="portfolio-title">{project.name}</h3>
                  <p className="portfolio-description">{project.description}</p>
                </div>
              </Card>
            </a>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-section">
        <div className="section-header">
          <h2 className="section-title">Why Choose Us</h2>
          <p className="section-subtitle">What makes us different</p>
        </div>

        <div className="why-grid">
          {whyChooseUs.map((item, index) => (
            <Card key={index} className="why-card glass-card">
              <CheckCircle2 className="why-icon" size={32} />
              <h3 className="why-title">{item.title}</h3>
              <p className="why-description">{item.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-header">
          <h2 className="section-title">What Our Clients Say</h2>
        </div>

        <div className="testimonials-slider">
          {testimonials.map((testimonial, index) => (
            <Card 
              key={index} 
              className={`testimonial-card glass-card ${index === activeTestimonial ? 'active' : ''}`}
            >
              <p className="testimonial-text">"{testimonial.text}"</p>

              <div className="testimonial-author">
                <div className="testimonial-avatar">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <div className="testimonial-name">{testimonial.name}</div>
                  <div className="testimonial-role">{testimonial.role}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="testimonial-dots">
          {testimonials.map((_, index) => (
            <button 
              key={index} 
              className={`dot ${index === activeTestimonial ? 'active' : ''}`} 
              onClick={() => setActiveTestimonial(index)} 
            />
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="contact-section">
        <div className="section-header fade-in-up">
          <h2 className="section-title">
            Start Your <span className="gradient-text">Project</span>
          </h2>
          <p className="section-subtitle">Let's build something amazing together</p>
        </div>

        <Card className="contact-card glass-card-premium neon-border spotlight fade-in-up stagger-2">
          <form onSubmit={handleSubmit} className="contact-form">

            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required 
                  className="glass-input" 
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required 
                  className="glass-input" 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Project Type</label>
                <Input 
                  value={formData.projectType}
                  onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                  placeholder="e.g., E-commerce, Portfolio, SaaS"
                  className="glass-input" 
                />
              </div>

              <div className="form-group">
                <label>Budget</label>
                <Input 
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  placeholder="e.g., $5,000 - $10,000"
                  className="glass-input" 
                />
              </div>
            </div>

            <div className="form-group">
              <label>Message</label>
              <Textarea 
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={5} 
                required 
                className="glass-input" 
              />
            </div>

            <Button type="submit" className="btn-primary submit-btn magnetic-button liquid-button ripple-effect">
              <Rocket size={20} />
              Start Your Project
              <ArrowRight size={20} />
            </Button>

            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "20px 0"
            }}>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.15)" }}></div>
              <span style={{ margin: "0 12px", color: "#fff", fontWeight: "600" }}>OR</span>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.15)" }}></div>
            </div>

            <Button
              type="button"
              onClick={() => window.open("https://forms.gle/w76MocALFuRVzEFc7", "_blank")}
              className="btn-primary magnetic-button liquid-button ripple-effect"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#00ffc8",
                color: "#000",
                fontWeight: "700",
              }}
            >
              Submit Through G-Form
            </Button>

          </form>
        </Card>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">

          <div className="footer-brand">
            <div className="footer-logo">aiwebAA</div>
            <p className="footer-tagline">Building the future of web, one pixel at a time</p>
          </div>

          <div className="footer-links">
            <div className="footer-column">
              <h4>Quick Links</h4>
              <a href="#services">Services</a>
              <a href="#portfolio">Portfolio</a>
              <a href="#contact">Contact</a>
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms">Terms & Conditions</a>
            </div>

            <div className="footer-column">
              <h4>Contact</h4>
              <div className="footer-contact">
                <Mail size={16} />
                <span>hello@aiwebaa.com</span>
              </div>
            </div>

            <div className="footer-column">
              <h4>Follow Us</h4>
              <div className="footer-socials">
                <a href="#" className="social-icon"><Linkedin size={20} /></a>
                <a href="#" className="social-icon"><Twitter size={20} /></a>
                <a href="#" className="social-icon"><Github size={20} /></a>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 aiwebAA. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
};

export default Home;      
